/**
 * ADR-0235 — Trust Engine slice 3: explainable verdicts for merchants
 * (pure, no I/O).
 *
 *   - `summariseAgentVerdicts` folds per-request shadow verdicts for ONE
 *     site into one summary per claimed agent (label, flag, counts,
 *     reason codes), applying overturned-dispute suppression.
 *   - `TRUST_REASON_COPY` maps every engine reason code to a plain-words
 *     sentence (Sill voice: precise, plain, no exclamation points). A test
 *     enumerates `TRUST_REASON_CODES`, so a new engine code without copy
 *     fails CI. Unknown codes fall back to a neutral sentence.
 *   - `matchAgentVerdict` attaches a summary to a dashboard agent row.
 *   - `merchantFeedbackWeight` caps how much one account's disputes can
 *     weigh as labelled examples (weak labels, per-account cap).
 *
 * Nothing here reads other sites' data: inputs are already scoped to one
 * site by the caller, and the output carries no evidence refs (feature
 * ids), addresses, hashes or networks — only closed reason codes, counts
 * and public registry facts (operator name, declared signer hosts).
 */
import { TRUST_REASON_CODES } from './types.js';
/** Labels a merchant can dispute. `suspicious` is a flag, not a label, but disputable. */
export const DISPUTABLE_LABELS = ['verified', 'likely_impersonator', 'suspicious'];
export function isDisputableLabel(v) {
    return typeof v === 'string' && DISPUTABLE_LABELS.includes(v);
}
export const TRUST_DISPUTE_STATUSES = ['open', 'upheld', 'overturned'];
/** Optional merchant note on a dispute. */
export const TRUST_DISPUTE_NOTE_MAX = 500;
/** Staff resolution note. */
export const TRUST_DISPUTE_RESOLUTION_NOTE_MAX = 1000;
/** Disputes one site may file per rolling 24 h. */
export const TRUST_DISPUTES_PER_SITE_PER_DAY = 10;
/** Total labelled-example weight one account can contribute per rule version. */
export const MERCHANT_FEEDBACK_ACCOUNT_WEIGHT_CAP = 5;
/** Claim keys are registry ids (`agent_…`) or `token:<lower product token>` (≤ 80). */
export const TRUST_CLAIM_KEY_RE = /^(?:agent_[a-z0-9_]{1,74}|token:[a-z0-9_][a-z0-9._-]{0,73})$/;
export const REASON_FAMILY = {
    sig_declared_signer: 'verified',
    published_range_attested: 'verified',
    rdns_confirmed: 'verified',
    sill_credential: 'verified',
    // ADR-0234 (slice 4) — Sill agent credentials.
    sill_credential_signature: 'verified',
    sill_credential_challenge: 'verified',
    credential_token_mismatch: 'likely_impersonator',
    credential_token_unlisted: 'unverified',
    sig_failed_pinned_key: 'likely_impersonator',
    signer_mismatch: 'likely_impersonator',
    ip_outside_list_and_asn: 'likely_impersonator',
    robots_token_ua: 'likely_impersonator',
    name_rotation: 'likely_impersonator',
    ip_outside_list_and_rdns: 'likely_impersonator',
    no_registered_claim: 'unverified',
    no_verification_method: 'unverified',
    proof_unavailable: 'unverified',
    deception_evidence_unattested: 'unverified',
    range_match_unattested: 'unverified',
    range_match_untrusted_ip: 'unverified',
    ip_outside_list_asn_inconclusive: 'unverified',
    ip_outside_list_not_edge_observed: 'unverified',
    // Slice 2 (ADR-0232). None of these accuse on their own, so none is in
    // the likely_impersonator family. Signature annotations describe how
    // strong a verification was, so they sit with the proof.
    rdns_mismatch: 'unverified',
    sig_expired: 'unverified',
    sig_unknown_key: 'unverified',
    sig_replayed: 'unverified',
    sig_nonce_unchecked: 'unverified',
    sig_reused: 'unverified',
    sig_key_pending: 'unverified',
    sig_window_long: 'verified',
    sig_ua_covered: 'verified',
    sig_no_nonce: 'verified',
    profile_network_mismatch: 'suspicious',
    profile_connection_mismatch: 'suspicious',
    profile_header_mismatch: 'suspicious',
    profile_probe_behaviour: 'suspicious',
    profile_insufficient: 'unverified',
    profile_drift: 'unverified',
};
function op(ctx) {
    return ctx.operator ?? 'its operator';
}
function opPossessive(ctx) {
    return ctx.operator === null ? "its operator's" : `${ctx.operator}'s`;
}
/**
 * Plain-words copy for every engine reason code. Keep each to one
 * sentence a merchant can read without knowing what a signature or a
 * network block is. No accuracy claims (VERIFIED_FACTS rule).
 */
export const TRUST_REASON_COPY = {
    sig_declared_signer: (c) => (c.signer !== null ? `Signed by ${c.signer}.` : `Carried a valid signature from a signer ${op(c)} declares.`),
    published_range_attested: (c) => `From ${opPossessive(c)} published network.`,
    rdns_confirmed: (c) => `Its address resolves to a hostname ${op(c)} owns, and back again.`,
    sill_credential: () => 'Passed a live identity check with a Sill-issued credential.',
    sill_credential_signature: () => 'Signed with a key its company registered with Sill.',
    sill_credential_challenge: () => 'Passed a live identity check from Sill, signed with a key its company registered with Sill.',
    credential_token_mismatch: () => 'Signed with a key registered by a different company from the one that uses this name.',
    credential_token_unlisted: () => "Signed with a key registered with Sill, but the company behind it hasn't registered this name. On its own that isn't proof of impersonation.",
    sig_failed_pinned_key: (c) => (c.signer !== null
        ? `Presented a signature in ${c.signer}'s name that did not check out.`
        : `Presented a signature in ${opPossessive(c)} name that did not check out.`),
    signer_mismatch: (c) => `Signed by someone other than ${opPossessive(c)} declared signer.`,
    ip_outside_list_and_asn: (c) => `Address doesn't belong to ${opPossessive(c)} network.`,
    robots_token_ua: () => 'Used a robots.txt-only name as its user agent. Real crawlers never send these names.',
    name_rotation: () => 'Part of a burst of fake AI names coming from the same addresses.',
    ip_outside_list_and_rdns: (c) => `Address is outside ${opPossessive(c)} published list, and its hostname doesn't belong to ${op(c)}.`,
    no_registered_claim: () => "The name isn't in Sill's agent registry, so there is nothing to check it against.",
    no_verification_method: (c) => `${c.operator ?? 'Its operator'} publishes no signature or address list, so this name can't be confirmed.`,
    proof_unavailable: () => 'No proof came with these requests: no valid signature and no address match.',
    deception_evidence_unattested: () => "Some signs pointed to impersonation, but the report came from an install that doesn't sign its reports, so Sill doesn't act on it.",
    range_match_unattested: (c) => `The address matched ${opPossessive(c)} network, but the report wasn't signed by your install.`,
    range_match_untrusted_ip: (c) => `The address matched ${opPossessive(c)} network, but it arrived through a proxy, so it isn't proof.`,
    ip_outside_list_asn_inconclusive: (c) => `The address is outside ${opPossessive(c)} published list, but Sill can't tell whose network it belongs to.`,
    ip_outside_list_not_edge_observed: (c) => `The address is outside ${opPossessive(c)} published list, but Cloudflare didn't see it directly, so it isn't proof.`,
    rdns_mismatch: (c) => `The address's hostname doesn't belong to ${op(c)}. On its own that isn't proof of impersonation.`,
    sig_expired: () => 'The signature had expired when the request arrived, so it was not accepted as proof. This can be a delay or a clock difference.',
    sig_unknown_key: (c) => `The signature used a key ${op(c)} doesn't list, so it was not accepted as proof. Keys sometimes change before the list updates.`,
    sig_replayed: () => 'The signature had already been used once, so it was not accepted as proof.',
    sig_nonce_unchecked: () => "Sill couldn't check whether the signature had been used before, so it was not accepted as proof.",
    sig_reused: () => 'The same signature showed up in more places than one real request could, so it was not accepted as proof.',
    sig_key_pending: (c) => `The signature used a key ${op(c)} started using very recently. Sill waits a while before accepting a new key as proof.`,
    sig_window_long: () => 'The signature stayed valid for longer than usual, which makes it a weaker proof.',
    sig_ua_covered: () => 'The signature also covered the agent name it sent, which makes it a stronger proof.',
    sig_no_nonce: () => 'The signature had no one-time value, so Sill cannot rule out that it was copied from another request.',
    profile_network_mismatch: (c) => `Came from a network the real ${c.name} doesn't use on your site.`,
    profile_connection_mismatch: (c) => `Connected differently from how the real ${c.name} connects to your site.`,
    profile_header_mismatch: (c) => `Sent a different set of request headers from the real ${c.name}.`,
    profile_probe_behaviour: (c) => `Asked for files the real ${c.name} doesn't request.`,
    profile_insufficient: () => 'Not enough verified history on your site yet to compare behaviour.',
    profile_drift: () => "This agent's verified traffic on your site changed recently, so Sill isn't comparing behaviour until it has re-learned it.",
};
export const UNKNOWN_REASON_COPY = 'Sill recorded a check that this version of the dashboard cannot describe yet.';
/** Copy for any string — unknown codes fall back safely (never throws, never echoes the code). */
export function reasonCopy(code, ctx) {
    if (!TRUST_REASON_CODES.includes(code))
        return UNKNOWN_REASON_COPY;
    return TRUST_REASON_COPY[code](ctx);
}
export function reasonFamily(code) {
    return TRUST_REASON_CODES.includes(code) ? REASON_FAMILY[code] : 'unverified';
}
export const TRUST_LABEL_TEXT = {
    verified: 'Verified',
    unverified: 'Unverified',
    likely_impersonator: 'Likely impersonator',
};
export const TRUST_SUSPICIOUS_TEXT = 'Suspicious';
export function trustLabelHeadline(label, name) {
    switch (label) {
        case 'verified': return `Sill confirmed that requests under this name came from ${name}.`;
        case 'likely_impersonator': return `Requests using ${name}'s name came from someone else.`;
        case 'unverified': return `Sill couldn't confirm who sent these requests. That on its own isn't a sign of bad intent.`;
    }
}
export const MAX_REASONS_PER_FAMILY = 6;
function maxIso(a, b) {
    return a > b ? a : b;
}
/**
 * Fold groups for one site into one summary per claim. Label precedence:
 *   impersonator requests and no verified ones → likely_impersonator;
 *   any verified request                     → verified (+ `impersonated`
 *                                               when fakes also used the name);
 *   otherwise                                → unverified.
 * Overturned disputes whose rule version equals `current_rule_version`
 * suppress the disputed label: its requests count as unverified, its
 * reasons are dropped, and the flag is cleared for `suspicious`.
 */
export function summariseAgentVerdicts(groups, reasons, overturned, current_rule_version) {
    const by = new Map();
    for (const g of groups) {
        const n = Number.isFinite(g.n) && g.n > 0 ? Math.floor(g.n) : 0;
        if (n === 0)
            continue;
        const a = by.get(g.claim_key) ?? {
            claim_key: g.claim_key, registry_agent_id: null, v: 0, u: 0, i: 0, s: 0,
            computed_at: g.computed_at, rule_version: g.rule_version, rv_at: g.computed_at,
        };
        if (a.registry_agent_id === null && g.registry_agent_id !== null)
            a.registry_agent_id = g.registry_agent_id;
        if (g.label === 'verified')
            a.v += n;
        else if (g.label === 'likely_impersonator')
            a.i += n;
        else
            a.u += n;
        if (g.suspicious)
            a.s += n;
        a.computed_at = maxIso(a.computed_at, g.computed_at);
        if (g.computed_at >= a.rv_at) {
            a.rv_at = g.computed_at;
            a.rule_version = g.rule_version;
        }
        by.set(g.claim_key, a);
    }
    const supp = new Map();
    for (const d of overturned) {
        if (d.rule_version !== current_rule_version)
            continue;
        const s = supp.get(d.claim_key) ?? new Set();
        s.add(d.disputed_label);
        supp.set(d.claim_key, s);
    }
    const reasonsBy = new Map();
    for (const r of reasons) {
        const n = Number.isFinite(r.n) && r.n > 0 ? Math.floor(r.n) : 0;
        if (n === 0)
            continue;
        const m = reasonsBy.get(r.claim_key) ?? new Map();
        m.set(r.reason, (m.get(r.reason) ?? 0) + n);
        reasonsBy.set(r.claim_key, m);
    }
    const out = [];
    for (const a of by.values()) {
        const s = supp.get(a.claim_key) ?? new Set();
        // Suppression only applies to verdicts from the rule version the
        // dispute was decided under; a newer rule re-opens the question.
        const active = a.rule_version === current_rule_version ? s : new Set();
        let { v, u, i, s: sus } = a;
        if (active.has('verified')) {
            u += v;
            v = 0;
        }
        if (active.has('likely_impersonator')) {
            u += i;
            i = 0;
        }
        if (active.has('suspicious'))
            sus = 0;
        const label = v > 0 ? 'verified' : i > 0 ? 'likely_impersonator' : 'unverified';
        const hidden = new Set();
        if (v === 0)
            hidden.add('verified');
        if (i === 0)
            hidden.add('likely_impersonator');
        if (sus === 0)
            hidden.add('suspicious');
        const fam = new Map();
        for (const [code, count] of reasonsBy.get(a.claim_key) ?? []) {
            const f = reasonFamily(code);
            if (hidden.has(f))
                continue;
            const list = fam.get(f) ?? [];
            list.push({ code, family: f, count });
            fam.set(f, list);
        }
        const order = ['likely_impersonator', 'suspicious', 'verified', 'unverified'];
        const reasonList = [];
        for (const f of order) {
            const list = (fam.get(f) ?? []).sort((x, y) => y.count - x.count || (x.code < y.code ? -1 : 1));
            reasonList.push(...list.slice(0, MAX_REASONS_PER_FAMILY));
        }
        out.push({
            claim_key: a.claim_key,
            registry_agent_id: a.registry_agent_id,
            label,
            suspicious: sus > 0,
            impersonated: label === 'verified' && i > 0,
            counts: { verified: v, unverified: u, likely_impersonator: i, suspicious: sus, total: v + u + i },
            reasons: reasonList,
            rule_version: a.rule_version,
            computed_at: a.computed_at,
            suppressed: [...active].sort(),
        });
    }
    out.sort((x, y) => y.counts.total - x.counts.total || (x.claim_key < y.claim_key ? -1 : 1));
    return out;
}
/** Is `label` currently shown on this summary (so a dispute of it makes sense)? */
export function isLabelShown(v, label) {
    if (label === 'suspicious')
        return v.suspicious;
    if (label === 'likely_impersonator')
        return v.label === 'likely_impersonator' || v.impersonated;
    return v.label === 'verified';
}
/**
 * Attach a verdict to a dashboard agent row: registry id first, then the
 * UA product token (`token:<lower name>`). Rows that match neither show
 * no trust chip.
 */
export function matchAgentVerdict(row, verdicts) {
    if (row.agent_id !== null && row.agent_id.length > 0) {
        const hit = verdicts.find((v) => v.registry_agent_id === row.agent_id || v.claim_key === row.agent_id);
        if (hit !== undefined)
            return hit;
    }
    const name = row.inferred_name?.trim().toLowerCase() ?? '';
    if (name.length > 0) {
        const hit = verdicts.find((v) => v.claim_key === `token:${name}`);
        if (hit !== undefined)
            return hit;
    }
    return null;
}
// ---------------------------------------------------------------------------
// Labelled examples (merchant feedback = weak label)
// ---------------------------------------------------------------------------
/**
 * Weight of a new merchant-feedback example given the weight this account
 * already contributed under the same rule version. Each example weighs at
 * most 1; one account never contributes more than the cap in total, so a
 * single merchant cannot steer the backtest.
 */
export function merchantFeedbackWeight(prior_account_weight, cap = MERCHANT_FEEDBACK_ACCOUNT_WEIGHT_CAP) {
    const prior = Number.isFinite(prior_account_weight) && prior_account_weight > 0 ? prior_account_weight : 0;
    const w = Math.min(1, cap - prior);
    return w > 0 ? Math.round(w * 1000) / 1000 : 0;
}
//# sourceMappingURL=merchant.js.map